import { useCallback, useEffect, useMemo, useState } from "react";
import { einheitImSatz, useT } from "../i18n";
import { Erklaerung, euro } from "./index";
import { Abschnitt } from "./Handyformen";
import { usePreis, useZubehoerAuswahl } from "../daten/haken";
import { postenAendern, type Merkposten } from "../daten/merkliste";
import type { Zubehoerlage } from "../daten/typen";
import { satzMalSchluessel } from "../daten/einmalbetrag";
import { anbauZuschlaege } from "../daten/anbauzuschlaege";
import type { Preiszuschlag } from "../daten/typen";

/* ══════════════════════════════════════════════════════════════════
   Die Anbauteile mit eigenem Tarif — in der Vorschau, nicht erst im
   Auftrag.

   Seit Migration 0078 legt `POST /vorgaenge/:id/positionen` für jedes
   gebundene Stück, dessen Typ im Stamm einen Tarif hat, eine **eigene
   Zeile** unter der Miete seines Trägers an (`routen/vorgaenge.ts`,
   `anbauteilePreisen`). Die alte Schnellerfassung rechnete sie in ihrer
   Vorschau mit; die Sammlung nicht — **der Auftrag stimmte, die Summe
   davor war zu klein** (docs/38-vermietmodul.md, „Was bis zum Umschalten
   noch fehlt", 3).

   Das ist die stille Abweichung, die Luca an anderer Stelle angestrichen
   hat: Eine Zahl, die sich nach dem Klick ändert, ist schlimmer als eine,
   die fehlt. Wer eine Fahrbahnplatte anhakt, liest oben denselben Betrag
   wie vorher und bekommt nach dem Anlegen einen anderen.

   **Gerechnet wird nicht hier, sondern geholt** (Regel 4). Je
   Anbauteiltyp eine Frage an `GET /preis` — mit dem Zeitraum des Trägers
   und der Kondition des Kunden, also genau das, was drüben die Zeile
   schreibt. Eine zweite Rechenstelle im Browser wäre die zweite Wahrheit.

   **Gezählt wird je Typ, nicht je Stück** (Regel 2): Zwei Fahrbahnplatten
   sind eine Zeile mit Menge zwei, und die API macht es genauso.

   **Das Ergebnis liegt am Posten** (`anbauNetto`), nicht im Gedächtnis
   dieses Bausteins: Die Karte in der Sammlung, der Fuß des Postenblatts
   und die Summe des Abschlusses rechnen alle mit `postenSumme` — drei
   Zahlen aus einer Quelle, statt einer Zahl an drei Stellen.

   Klassen: `anb-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Ein Anbauteiltyp, der an diesem Posten hängt — und wie viele Stück davon. */
type Anbauteil = { typId: string; label: string; stuecke: number };

/**
 * Was die Preisfindung zu einem Anbauteiltyp sagt — für **ein** Stück.
 *
 * `einzelpreis` und `rabattProzent` stehen neben `netto`, weil die Stückzahl
 * erst hier dazukommt: `netto` ist auf den Cent gerundet, und wer es
 * malnimmt, rundet zweimal. Gerechnet wird darum wie in der Annahme
 * (`dienste/anbauteile.ts`) — Menge mal Stückzahl mal Satz, einmal gerundet.
 */
type Tarif = {
  menge: number; einheitKey: string; netto: number;
  einzelpreis: number; rabattProzent: number;
  /** Die Zuschläge der Auskunft für dieses Anbauteil — sie stehen auf dem Auftrag an seiner Zeile (P6-03). */
  zuschlaege: Preiszuschlag[];
};

/**
 * Die gebundenen Stücke mit eigenem Tarif, je Typ gezählt.
 *
 * Ein Stück wird höchstens einmal gezählt: Derselbe Anbauteiltyp kann am
 * Träger zweimal zugeordnet sein — einmal frei, einmal an eine Ausstattung
 * gebunden —, und dasselbe Stück stünde dann in beiden Zeilen.
 */
export function anbauteileAus(
  lage: Zubehoerlage[] | undefined,
  gebunden: string[],
): Anbauteil[] {
  if (!lage?.length || !gebunden.length) return [];
  const jeTyp = new Map<string, Anbauteil>();
  const gezaehlt = new Set<string>();
  lage.filter((z) => z.eigener_tarif).forEach((z) => {
    z.stuecke.forEach((s) => {
      if (!gebunden.includes(s.id) || gezaehlt.has(s.id)) return;
      gezaehlt.add(s.id);
      const da = jeTyp.get(z.zubehoer_typ_id);
      if (da) da.stuecke += 1;
      else jeTyp.set(z.zubehoer_typ_id, { typId: z.zubehoer_typ_id, label: z.label, stuecke: 1 });
    });
  });
  return [...jeTyp.values()].sort((a, b) => a.label.localeCompare(b.label, "de"));
}

/**
 * Der Tarif eines Anbauteiltyps — eine Komponente, die nichts zeichnet.
 *
 * Fragen lassen sich nicht in einer Schleife stellen, also fragt je Typ eine
 * eigene und meldet ihr Ergebnis zurück. Derselbe Weg, den die alte
 * Schnellerfassung dafür ging (`Mietpreis`).
 */
function Anbautarif({ typId, von, bis, kunde, melden }: {
  typId: string;
  von: string;
  bis: string;
  kunde: string;
  melden: (typId: string, tarif: Tarif) => void;
}) {
  const { data } = usePreis(typId, von, bis, kunde || null);
  useEffect(() => {
    if (!data) return;
    melden(typId, {
      menge: data.menge, einheitKey: data.einheit_key, netto: data.netto,
      einzelpreis: data.einzelpreis, rabattProzent: data.rabatt_prozent,
      zuschlaege: data.zuschlaege,
    });
  }, [data, typId, melden]);
  return null;
}

/**
 * Was an diesem Posten als eigene Zeile mit hinausgeht.
 *
 * Steht kein Anbauteil mit eigenem Tarif daran, zeichnet der Baustein nichts
 * — auch keine Überschrift. Der Regelfall hat keines.
 *
 * ```tsx
 * <Anbauteile p={posten} kunde={wer.nr} />
 * ```
 */
export function Anbauteile({ p, kunde, handy = false }: {
  p: Merkposten;
  /** Die Kundennummer — seine Kondition rechnet im Tarif mit. Leer geht auch. */
  kunde: string;
  /**
   * **Im Blatt am Telefon** (20.09.2026): ein eigener Abschnitt mit einer
   * Karte, die Zeilen darin im Hausmaß (`stile/telefon/positionen.css`,
   * `.posb .anb-zeile`). Dieselbe Rechnung, dieselben Abfragen — nur ohne
   * das Schild mit Fragezeichen, das der Abschnittskopf ersetzt.
   */
  handy?: boolean;
}) {
  const t = useT();
  const gebunden = useMemo(() => p.zubehoer ?? [], [p.zubehoer]);

  /* **Gefragt wird nur, wo etwas gebunden ist.** Ohne Zubehör am Posten gibt
     es keine Anbauzeile, und eine Abfrage für jeden Radlader wäre eine
     Abfrage auf eine Antwort, die schon feststeht. Der Schlüssel ist
     derselbe wie in `ZubehoerWahl` — im Postenblatt kostet das nichts. */
  const { data: lage } = useZubehoerAuswahl(
    gebunden.length ? p.typId : undefined, p.von, p.bis,
  );
  const teile = useMemo(() => anbauteileAus(lage, gebunden), [lage, gebunden]);

  const [tarife, setzeTarife] = useState<Record<string, Tarif>>({});
  /* Nur, wenn sich wirklich etwas ändert: Sonst meldet jede Meldung die
     nächste. */
  const melden = useCallback((typId: string, tarif: Tarif) => {
    setzeTarife((alt) => {
      const da = alt[typId];
      if (da && da.menge === tarif.menge && da.einheitKey === tarif.einheitKey
          && da.netto === tarif.netto && da.einzelpreis === tarif.einzelpreis
          && da.rabattProzent === tarif.rabattProzent
          && anbauZuschlaege(da.zuschlaege, 1) === anbauZuschlaege(tarif.zuschlaege, 1)
          && da.zuschlaege.length === tarif.zuschlaege.length) {
        return alt;
      }
      return { ...alt, [typId]: tarif };
    });
  }, []);

  /* **Derselbe Ausdruck wie in der Annahme** (`dienste/anbauteile.ts`,
     18.09.2026): Menge mal Stückzahl mal Satz, dann der Rabatt, dann
     **einmal** gerundet. `tarif.netto * stuecke` wäre bequemer und weicht bei
     mehr als einem Stück um einen Cent ab — `netto` ist schon auf den Cent
     gerundet, und die Hausregel rundet auf Positionsebene, nicht auf
     Stückebene (`packages/fach/src/typen.ts`). Genau solche stillen
     Abweichungen sind der Grund, warum diese Vorschau überhaupt gebaut
     wurde. */
  const betragVon = (a: Anbauteil): number | null => {
    const tarif = tarife[a.typId];
    if (tarif === undefined) return null;
    const roh = tarif.menge * a.stuecke * tarif.einzelpreis * (1 - tarif.rabattProzent / 100);
    return Math.round(roh * 100 + 1e-9) / 100;
  };

  /* **Geschrieben wird erst, wenn alles dasteht.** Ein Betrag, der während
     des Ladens auf die Hälfte fällt und eine Sekunde später zurückspringt,
     ist die Sorte Zahl, die niemand mehr glaubt. */
  const fertig = gebunden.length === 0
    || (Boolean(lage) && teile.every((a) => tarife[a.typId] !== undefined));
  /* **Und ihre Zuschläge** (P6-03, 05.10.2026). Auf dem Auftrag trägt die
     Zubehörzeile ihre eigene Versicherung und ihren Rücknahme-Check — die
     Vorschau nannte 73,00, Auftrag und AB 98,50. Ausgewählt in
     `daten/anbauzuschlaege.ts`, gerechnet von der Preisauskunft. */
  const zuschlagVon = (a: Anbauteil): number => {
    const tarif = tarife[a.typId];
    return tarif === undefined ? 0 : anbauZuschlaege(tarif.zuschlaege, a.stuecke);
  };
  const summe = teile.reduce((s, a) => s + (betragVon(a) ?? 0) + zuschlagVon(a), 0);
  const neu = teile.length ? Math.round(summe * 100) / 100 : undefined;

  useEffect(() => {
    if (!fertig || p.anbauNetto === neu) return;
    postenAendern(p.id, { anbauNetto: neu });
  }, [fertig, neu, p.anbauNetto, p.id]);

  if (!teile.length) return null;

  const zeilen = teile.map((a) => {
    const tarif = tarife[a.typId];
    const betrag = betragVon(a);
    return (
      <p className="anb-zeile" key={a.typId}>
        <span className="anb-was">
          {a.stuecke > 1 ? t("merkliste.anbauMal", { was: a.label, zahl: a.stuecke }) : a.label}
        </span>
        <span className="anb-wie">
          {tarif
            ? t(satzMalSchluessel(tarif.einheitKey, "merkliste.jeEinheitMal", "merkliste.jeEinheitMalPauschal"), {
              einheit: einheitImSatz(t, tarif.einheitKey, tarif.menge * a.stuecke), menge: tarif.menge * a.stuecke,
            })
            : ""}
        </span>
        <b className="anb-betrag">{betrag === null ? t("allgemein.laedt") : euro(betrag)}</b>
        {zuschlagVon(a) > 0 ? (
          <span className="anb-wie anb-zu">{t("merkliste.anbauZuschlaege", { betrag: euro(zuschlagVon(a)) })}</span>
        ) : null}
      </p>
    );
  });
  /* Die Fragen selbst — sie zeichnen nichts. */
  const fragen = teile.map((a) => (
    <Anbautarif
      key={`t:${a.typId}`}
      typId={a.typId}
      von={p.von}
      bis={p.bis}
      kunde={kunde}
      melden={melden}
    />
  ));

  if (handy) {
    return (
      <Abschnitt titel={t("merkliste.anbauteile")}>
        <div className="hf-zeilenblatt anb">{zeilen}</div>
        {fragen}
      </Abschnitt>
    );
  }

  return (
    <div className="anb">
      <span className="feld-schild anb-schild">
        {t("merkliste.anbauteile")}
        <Erklaerung k="zubehoer.eigenerTarifErklaerung" />
      </span>
      {zeilen}
      {fragen}
    </div>
  );
}
